import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AuthFacadeService } from '../../services';
import { AUTH_ACTIONS } from '../actions/auth.actions';
import { ConfirmationService } from 'primeng/api';
import { ApplicationService } from '../../services/application.service';
import { APPLICATION_ACTIONS } from '../actions/application.actions';
import { CLAIMS_ACTIONS } from '../actions/claims.actions';
import { ACCOUNTS_ACTIONS } from '../actions/accounts.actions';
import { SITES_ACTIONS } from '../actions/sites.actions';
import { Store } from '@ngrx/store';
import { AUTH_SELECTORS } from '../selectors/auth.selectors';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly authServiceFacade: AuthFacadeService,
    private readonly confirmService: ConfirmationService,
    private readonly applicationService: ApplicationService,
    private readonly store: Store,
  ) {}

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_ACTIONS.login),
        tap(() => this.authServiceFacade.login()),
      ),
    { dispatch: false },
  );

  initLoginCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_ACTIONS.initLoginCheck, AUTH_ACTIONS.login),
      map(() => APPLICATION_ACTIONS.showLoader()),
    ),
  );

  authorized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_ACTIONS.authenticated),
      switchMap(() => [CLAIMS_ACTIONS.getClaims(), ACCOUNTS_ACTIONS.getAccounts(), SITES_ACTIONS.getAllSites()]),
    ),
  );

  pushUserInfoToDataLayer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_ACTIONS.authenticated),
        withLatestFrom(this.store.select(AUTH_SELECTORS.userId), this.store.select(AUTH_SELECTORS.userName)),
        filter(([, id, name]) => !!id && !!name),
        tap(([, id, full_name]) => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'userAuthenticated',
            userId: `F${id}`,
            userName: full_name,
          });
        }),
      ),
    { dispatch: false },
  );

  loginNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_ACTIONS.loginNotification),
        tap(() => {
          this.confirmService.close();
          void this.authServiceFacade.setAutomaticRefreshToken();
          void this.authServiceFacade.setSessionChecksIfStillLoggedIn();
        }),
      ),
    { dispatch: false },
  );

  sessionTerminated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_ACTIONS.sessionTerminated, AUTH_ACTIONS.logoutNotification),
        tap(() =>
          this.confirmService.confirm({
            header: 'Confirmation',
            message: 'Your session has expired. Please sign in again.',
            rejectVisible: false,
            acceptLabel: 'Ok',
            accept: () => window.location.reload(),
          }),
        ),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_ACTIONS.logout),
      tap(() => {
        this.applicationService.saveActiveUrlToStorage();
        this.authServiceFacade.logout();
      }),
      map(() => APPLICATION_ACTIONS.showLoader()),
    ),
  );
}
